/**
 * Rescue Next.js page-data fetches without touching anyone else's fetch.
 *
 * Vercel's edge cache sometimes 500s on `/_next/data/<build>/<route>.json`.
 * Next.js reads that as an asset error and hard-navigates, which the user sees
 * as a black screen. The guard answers a failed page-data request with an empty
 * but valid payload so the page mounts and its own API fallback loads the data.
 *
 * The guard has to sit on `window.fetch`, which means every fetch on the page
 * goes through it -- ours, the ad SDK's, the consent script's, the translate
 * widget's. That matters for more than performance: the wrapper used to `await`
 * the underlying call, so when a vendor's cross-origin fetch rejected, V8 spliced
 * this module's frame onto the top of the rejection's async stack. Our
 * `unhandledrejection` handler classifies by the topmost frame
 * (utils/error-tracking.ts `isFromOwnScripts`), so a vendor's "Failed to fetch"
 * arrived wearing our chunk's filename and filed as an application error --
 * 21 rows from a single user on 2026-09-01, and the reason the third-party
 * fetch-failure noise rule had to exist at all.
 *
 * So: a request this guard does not manage is handed to the browser untouched
 * and its promise is returned as-is. No await, no `.then`, no frame of ours in
 * anybody else's stack.
 */

type FetchArgs = Parameters<typeof fetch>;
type FetchLike = (...args: FetchArgs) => Promise<Response>;

/** The one request shape this guard exists to rescue. */
export function isNextDataRequest(url: string): boolean {
  return url.includes('/_next/data/') && url.endsWith('.json');
}

/** Best-effort URL of a fetch call, for routing only. */
export function requestUrlOf(input: FetchArgs[0]): string {
  if (typeof input === 'string') return input;
  if (input instanceof URL) return input.href;
  return (input as Request)?.url || '';
}

/**
 * Patch `window.fetch` so failed page-data requests degrade instead of blanking
 * the page. Returns the uninstall function.
 */
export function installNextDataFetchGuard(
  win: { fetch: FetchLike },
  onPageDataFailure: () => void
): () => void {
  const originalFetch = win.fetch;

  const guarded: FetchLike = (...args: FetchArgs) => {
    if (!isNextDataRequest(requestUrlOf(args[0]))) {
      // Not ours to manage: the caller gets the browser's own promise, so a
      // rejection keeps the caller's stack and none of ours.
      return originalFetch(...args);
    }

    return originalFetch(...args).then((response) => {
      if (response.ok) return response;
      onPageDataFailure();
      return new Response(JSON.stringify({ pageProps: {} }), {
        status: 200,
        headers: { 'Content-Type': 'application/json' },
      });
    });
  };

  win.fetch = guarded;
  return () => {
    win.fetch = originalFetch;
  };
}
